import { HttpClient } from '@angular/common/http';
import { inject } from '@angular/core';
import { ResolveFn } from '@angular/router';
import { TranslateService, TranslationObject } from '@ngx-translate/core';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

export interface TranslationSource {
	path: string;
	prefix?: string;
}

export function resolveFeatureTranslations(sources: TranslationSource[]): ResolveFn<void> {
	return (): Observable<void> => {
		const http = inject(HttpClient);
		const translate = inject(TranslateService);
		const lang = translate.getCurrentLang() ?? translate.getFallbackLang() ?? 'tr';

		return forkJoin(
			sources.map((source) =>
				http.get<TranslationObject>(`${source.path}${lang}.json`).pipe(
					map((t) => (source.prefix ? { [source.prefix]: t } : t)),
					catchError(() => of({}))
				)
			)
		).pipe(
			map((results) => results.reduce((acc, curr) => deepMerge(acc, curr), {})),
			tap((translations) => translate.setTranslation(lang, translations, true)),
			map(() => undefined)
		);
	};
}

function deepMerge(target: object, source: object): object {
	for (const key of Object.keys(source)) {
		const sv = source[key];
		const tv = target[key];
		if (sv && typeof sv === 'object' && !Array.isArray(sv) && tv && typeof tv === 'object' && !Array.isArray(tv)) {
			target[key] = deepMerge(tv, sv);
		} else {
			target[key] = sv;
		}
	}
	return target;
}
